/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useRef } from 'react';
import { createOptionsChart, HistogramSeries } from 'lightweight-charts';

const DEFAULT_HISTOGRAM_HEIGHT = 300;
const DEFAULT_HISTOGRAM_BIN_COUNT = 20;
const DEFAULT_HISTOGRAM_COLOR = '#26a69a';
const DEFAULT_HISTOGRAM_BACKGROUND = '#1a1e26';
const DEFAULT_HISTOGRAM_TEXT = '#d1d5db';
const DEFAULT_HISTOGRAM_GRID = 'rgba(70, 130, 180, 0.5)';

const getHistogramPresentation = (schemaComponent) => {
  const presentation = schemaComponent?.presentation;
  if (!presentation || typeof presentation !== 'object' || Array.isArray(presentation)) {
    return null;
  }

  return presentation;
};

const getFiniteNumber = (value, fallback, minimum = 0) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(value, minimum);
};

const getHistogramColor = (value, presentation) => {
  if (value > 0 && typeof presentation?.positive_color === 'string') {
    return presentation.positive_color;
  }
  if (value < 0 && typeof presentation?.negative_color === 'string') {
    return presentation.negative_color;
  }
  if (value === 0 && typeof presentation?.zero_color === 'string') {
    return presentation.zero_color;
  }
  if (typeof presentation?.series_color === 'string') {
    return presentation.series_color;
  }

  return DEFAULT_HISTOGRAM_COLOR;
};

const buildHistogramData = (data, presentation) => {
  const numericData = Array.isArray(data)
    ? data.filter((value) => typeof value === 'number' && Number.isFinite(value))
    : [];

  if (numericData.length === 0) {
    return [];
  }

  const minimumValue = Math.min(...numericData);
  const maximumValue = Math.max(...numericData);

  if (minimumValue === maximumValue) {
    return [{
      time: minimumValue,
      value: numericData.length,
      color: getHistogramColor(minimumValue, presentation),
    }];
  }

  const requestedBinCount = Math.round(getFiniteNumber(
    presentation?.bin_count,
    DEFAULT_HISTOGRAM_BIN_COUNT,
    1,
  ));
  const binSize = (maximumValue - minimumValue) / requestedBinCount;
  const bins = new Map();

  numericData.forEach((value) => {
    const binIndex = Math.min(
      requestedBinCount - 1,
      Math.max(0, Math.floor((value - minimumValue) / binSize)),
    );
    const binStart = minimumValue + (binIndex * binSize);
    bins.set(binStart, (bins.get(binStart) || 0) + 1);
  });

  return Array.from(bins.entries())
    .map(([binStart, count]) => ({
      time: binStart,
      value: count,
      color: getHistogramColor(binStart + (binSize / 2), presentation),
    }))
    .sort((left, right) => left.time - right.time);
};

const HistogramChart = ({ data, title, schemaComponent }) => {
  const chartContainerRef = useRef(null);
  const presentation = getHistogramPresentation(schemaComponent);

  useEffect(() => {
    const histogramData = buildHistogramData(data, presentation);
    if (!chartContainerRef.current || histogramData.length === 0) return;

    const chart = createOptionsChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: Math.round(getFiniteNumber(presentation?.height, DEFAULT_HISTOGRAM_HEIGHT, 120)),
      layout: {
        backgroundColor: presentation?.background_color || DEFAULT_HISTOGRAM_BACKGROUND,
        textColor: presentation?.text_color || DEFAULT_HISTOGRAM_TEXT,
      },
      grid: {
        vertLines: {
          color: presentation?.grid_color || DEFAULT_HISTOGRAM_GRID,
        },
        horzLines: {
          color: presentation?.grid_color || DEFAULT_HISTOGRAM_GRID,
        },
      },
    });

    const histogramSeries = chart.addSeries(HistogramSeries, {
      color: typeof presentation?.series_color === 'string'
        ? presentation.series_color
        : DEFAULT_HISTOGRAM_COLOR,
      priceFormat: {
        type: 'volume',
      },
    });

    histogramSeries.setData(histogramData);

    chart.timeScale().fitContent();

    const handleResize = () => {
      chart.applyOptions({ width: chartContainerRef.current.clientWidth });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, presentation]);

  return (
    <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
      <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
      {typeof presentation?.description === 'string' && presentation.description ? (
        <p className="mb-2 text-sm text-textSecondary">{presentation.description}</p>
      ) : null}
      <div ref={chartContainerRef} />
    </div>
  );
};

export default HistogramChart;
